import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TopMenu from "./TopMenu";
import PageGuide from "./components/PageGuide";
import {
  Box,
  Button,
  SimpleGrid,
  Image,
  Text,
  Heading,
  HStack,
  VStack,
  LinkBox,
  LinkOverlay,
  Fade,
  Icon,
  Avatar,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiBookOpen,
  FiCalendar,
  FiClipboard,
  FiFileText,
} from "react-icons/fi";
import {
  UiButton,
  UiInput,
  UiTabs,
  UiTabList,
  UiTabPanels,
  UiTab,
  UiTabPanel,
} from "./components/ui";
import { ACTIVITY_KINDS, listActivity } from "./services/activityService";
import { format } from "date-fns";

const KIND_META = {
  event: { label: "Event", color: "blue.500", icon: FiCalendar },
  challenge: { label: "Challenge", color: "orange.500", icon: FiClipboard },
  document: { label: "Document", color: "purple.500", icon: FiFileText },
  publication: { label: "Publication", color: "gwbi.500", icon: FiBookOpen },
};

const TABS = [
  { key: "all", kinds: ACTIVITY_KINDS },
  { key: "event", kinds: ["event"] },
  { key: "challenge", kinds: ["challenge"] },
  { key: "document", kinds: ["document"] },
  { key: "publication", kinds: ["publication"] },
];

const PAGE_SIZE = 30;

const parseDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = parseDate(value);
  if (!d) return "";
  return format(d, "EEE d MMMM yyyy");
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function Feed({ onLogout }) {
  const { t } = useTranslation("common");

  const bg = "surface.canvas";
  const cardBg = "surface.card";
  const cardText = "gray.800";
  const cardMuted = "text.muted";
  const borderCol = "border.default";
  const hoverCardStyle = {
    boxShadow: "ui-md",
    bg: "surface.subtle",
    transform: "translateY(-4px)",
    borderColor: "gwbi.300",
  };

  const [tabIndex, setTabIndex] = useState(0);
  const [itemsByTab, setItemsByTab] = useState({});
  const [loadingByTab, setLoadingByTab] = useState({});
  const [loadedByTab, setLoadedByTab] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCounts, setVisibleCounts] = useState({});

  const activeTab = TABS[tabIndex] || TABS[0];

  const loadTab = useCallback(
    async (tab) => {
      const key = tab.key;
      if (loadedByTab[key]) return;
      setLoadingByTab((p) => ({ ...p, [key]: true }));
      try {
        const data = await listActivity({
          kinds: tab.kinds,
          perSource: 25,
          limit: 100,
        });
        setItemsByTab((p) => ({
          ...p,
          [key]: Array.isArray(data?.items) ? data.items : [],
        }));
        setLoadedByTab((p) => ({ ...p, [key]: true }));
      } catch (err) {
        console.error("[Feed] loadTab error", key, err);
      } finally {
        setLoadingByTab((p) => ({ ...p, [key]: false }));
      }
    },
    [loadedByTab],
  );

  useEffect(() => {
    loadTab(activeTab);
  }, [activeTab, loadTab]);

  const handleLoadMore = (key) =>
    setVisibleCounts((p) => ({
      ...p,
      [key]: (p[key] || PAGE_SIZE) + PAGE_SIZE,
    }));

  // ── Card component ──
  const FeedCard = ({ item }) => {
    const meta = KIND_META[item.kind] || {
      label: item.kind,
      color: "gray.500",
      icon: FiFileText,
    };
    const CardIcon = meta.icon;
    const href =
      item.link && /^https?:/i.test(item.link) ? item.link : undefined;
    const internalTo =
      item.link && !href ? item.link : undefined;

    return (
      <LinkBox
        as="article"
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderCol}
        overflow="hidden"
        transition="all 0.2s ease"
        _hover={hoverCardStyle}
        display="flex"
        flexDirection="column"
        h="100%"
      >
        {/* Image / icon area */}
        <Box
          position="relative"
          w="100%"
          pt="56%"
          bg="surface.subtle"
          overflow="hidden"
        >
          {item.image_url ? (
            <Image
              src={item.image_url}
              alt={item.title || ""}
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              objectFit="cover"
            />
          ) : (
            <Box
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={meta.color}
            >
              <Icon as={CardIcon} boxSize={10} opacity={0.35} />
            </Box>
          )}
          {/* Pill badge */}
          <Box
            position="absolute"
            top={2}
            left={2}
            bg={cardBg}
            borderRadius="full"
            px={2.5}
            py={0.5}
            fontSize="2xs"
            fontWeight="bold"
            color={meta.color}
            boxShadow="sm"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {meta.label}
          </Box>
        </Box>

        {/* Body */}
        <VStack
          align="stretch"
          spacing={1.5}
          p={3}
          flex="1"
          minH={0}
        >
          {/* Date */}
          {item.happened_at && (
            <Text fontSize="2xs" color={cardMuted}>
              {formatDate(item.happened_at) || formatRelative(item.happened_at)}
            </Text>
          )}

          {/* Title */}
          <LinkOverlay
            href={href}
            {...(internalTo ? { as: "a", href: internalTo } : {})}
            target={href ? "_blank" : undefined}
            rel={href ? "noopener noreferrer" : undefined}
          >
            <Text
              fontWeight="semibold"
              fontSize="sm"
              color={cardText}
              noOfLines={3}
              lineHeight="short"
            >
              {item.title || t("feed.untitled") || "Untitled"}
            </Text>
          </LinkOverlay>

          {/* Snippet */}
          {item.snippet && (
            <Text fontSize="xs" color={cardMuted} noOfLines={3}>
              {item.snippet}
            </Text>
          )}

          {/* Scope tag */}
          {item.scope && item.team_key && (
            <Text fontSize="2xs" color="purple.500" fontWeight="medium">
              {item.scope}/{item.team_key}
            </Text>
          )}

          {/* Actor */}
          {item.actor_name && (
            <HStack spacing={1.5} pt={1} mt="auto">
              <Avatar
                size="2xs"
                name={item.actor_name}
                src={item.actor_avatar || undefined}
              />
              <Text fontSize="2xs" color={cardMuted}>
                {item.actor_name}
              </Text>
            </HStack>
          )}
        </VStack>
      </LinkBox>
    );
  };

  const GridSection = ({ items }) => (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} gap={4}>
      {items.map((it) => (
        <FeedCard key={`${it.kind}-${it.id}`} item={it} />
      ))}
    </SimpleGrid>
  );

  const renderTabContent = (tab) => {
    const key = tab.key;
    const loading = loadingByTab[key];
    const loaded = loadedByTab[key];
    const allItems = itemsByTab[key] || [];
    const visible = visibleCounts[key] || PAGE_SIZE;

    const term = searchTerm.trim().toLowerCase();
    const filteredItems = term
      ? allItems.filter((it) => {
          const title = (it.title || "").toLowerCase();
          const snippet = (it.snippet || "").toLowerCase();
          const actor = (it.actor_name || "").toLowerCase();
          const scope = (it.team_key || "").toLowerCase();
          return (
            title.includes(term) ||
            snippet.includes(term) ||
            actor.includes(term) ||
            scope.includes(term)
          );
        })
      : allItems;

    if (loading && !loaded) {
      return (
        <Text color={cardMuted}>
          {t("card_view.loading") || "Loading..."}
        </Text>
      );
    }
    if (!loading && loaded && filteredItems.length === 0) {
      return (
        <Text color={cardMuted}>
          {term
            ? t("dashboard.no_matches") || "No matches for this search."
            : t("feed.no_activity") || "No activity yet."}
        </Text>
      );
    }

    return (
      <>
        <GridSection items={filteredItems.slice(0, visible)} />
        {filteredItems.length > visible && (
          <UiButton
            mt={4}
            size="xs"
            height="26px"
            px={3}
            variant="primary"
            fontSize="xs"
            fontWeight="semibold"
            alignSelf="center"
            onClick={() => handleLoadMore(key)}
            isLoading={loading}
          >
            {t("card_view.show_more") || "Load 30 more"}
          </UiButton>
        )}
      </>
    );
  };

  return (
    <Box minH="100vh" bg={bg}>
      <TopMenu onLogout={onLogout} />
      <Box px={{ base: 3, md: 6 }} py={4}>
        <HStack spacing={3} align="center" mb={3}>
          <Button
            as="a"
            href="/dashboard"
            size="sm"
            variant="ghost"
            color="gray.600"
            _hover={{ bg: "gray.100" }}
            leftIcon={<FiArrowLeft />}
          >
            Back
          </Button>
          <Heading size="md">
            {t("feed.heading") || "Activity Feed"}
          </Heading>
          <PageGuide id="feed" />
        </HStack>
        <Text fontSize="sm" color={cardMuted} mb={4}>
          {t("feed.description") ||
            "Events, challenges, documents, and publications across themes and regions."}
        </Text>

        {/* Search */}
        <Box mb={4}>
          <HStack spacing={3} align="center">
            <UiInput
              placeholder={
                t("feed.search_placeholder") ||
                "Search activity..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              w="100%"
              maxW="400px"
            />
            {searchTerm && (
              <UiButton
                size="sm"
                variant="ghost"
                onClick={() => setSearchTerm("")}
              >
                {t("card_view.clear_search") || "Clear"}
              </UiButton>
            )}
          </HStack>
        </Box>

        {/* Tabs */}
        <UiTabs
          isFitted
          variant="enclosed"
          index={tabIndex}
          onChange={setTabIndex}
        >
          <UiTabList>
            <UiTab
              _selected={{ fontWeight: "bold", color: "gray.900" }}
              color="text.muted"
            >
              {t("feed.tab_all") || "All"}
            </UiTab>
            <UiTab
              _selected={{ fontWeight: "bold", color: "gray.900" }}
              color="text.muted"
            >
              {t("feed.tab_events") || "Events"}
            </UiTab>
            <UiTab
              _selected={{ fontWeight: "bold", color: "gray.900" }}
              color="text.muted"
            >
              {t("feed.tab_challenges") || "Challenges"}
            </UiTab>
            <UiTab
              _selected={{ fontWeight: "bold", color: "gray.900" }}
              color="text.muted"
            >
              {t("feed.tab_documents") || "Documents"}
            </UiTab>
            <UiTab
              _selected={{ fontWeight: "bold", color: "gray.900" }}
              color="text.muted"
            >
              {t("feed.tab_publications") || "Publications"}
            </UiTab>
          </UiTabList>
          <UiTabPanels mt={4}>
            {TABS.map((tab) => (
              <UiTabPanel key={tab.key} px={0}>
                <Fade in>{renderTabContent(tab)}</Fade>
              </UiTabPanel>
            ))}
          </UiTabPanels>
        </UiTabs>
      </Box>
    </Box>
  );
}
